import { RowSelectedEvent } from '@ag-grid-community/core';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { GetOrderHeadParams, OrderHeadGet, OrderheadService, OrderLineLegacyItem } from '@app/services/orderhead.service';
import { OrderLineItem } from '../order-lines-grid/order-lines-grid.component';

@Component({
    selector: 'tt-order-head-order-lines',
    templateUrl: './order-head-order-lines.component.html',
    styleUrls: ['./order-head-order-lines.component.css'],
    standalone: false,
})
export class OrderHeadOrderLinesComponent implements OnChanges {
    @Input()
    public params?: GetOrderHeadParams;

    @Input()
    public get?: OrderHeadGet;

    public displayType: OrderLineDisplayType = 'std';

    public orderLines: OrderLineLegacyItem[] = [];

    public oldGrid?: GridOptions;

    public standardGrid?: GridOptions<OrderLineProvideMethodRow>;

    public changeGrid?: GridOptions<OrderLineProvideMethodRow>;

    public dynamicGrid?: GridOptions<OrderLineItem>;

    public selectedOrderlineKeyno?: string | number;

    public calculationRefreshToken = 0;

    public generatingErrorCheck = false;

    public changingProvideMethod = false;

    public addingProductsFromPricelist = false;

    public removingZeroQuantityLines = false;

    public updatingOrderPrices = false;

    public readonly mconfig = this.createMconfig();

    constructor(
        private orderhead: OrderheadService,
        private modal: ModalService,
        private state: StateService,
        private remember: RememberService,
        private utility: UtilityService,
    ) {}

    public onDisplayTypeChanged() {
        this.readCurrentBranch();
    }

    public async generateCheck() {
        if (!this.get || this.generatingErrorCheck) return;

        try {
            this.generatingErrorCheck = true;

            if (this.orderLines.length === 0) {
                await this.readLegacyOrderLines();
            }

            const lines = this.orderLines.filter((line) => +line.item_id > 0 && +line.item_id < 9999999990);
            const progressDialog = this.modal.openProgressDialog({ value: 0, max: lines.length, label: 'progressbar', disableClose: true });
            let value = 0;

            for (const line of lines) {
                await progressDialog.setLabel(`Name: ${line.item_name}`, false);

                const response = await this.orderhead.generateOrderLineItems({ orderline_keyno: line.item_id });

                if ('' + response.errorcode !== '0') {
                    progressDialog.updateInvalidText(response.errormessage);
                    progressDialog.updateStatus('invalid');
                    break;
                }

                value = value + 1;
                progressDialog.updateValue(value);
            }

            progressDialog.dialogRef.disableClose = false;

            if (value === lines.length) {
                progressDialog.updateStatus('finish');
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.generatingErrorCheck = false;
        }
    }

    public async openNewOrderLine(event?: MouseEvent) {
        if (!this.get) return;

        try {
            const lockStatus = await this.orderhead.getOrderHeadLockStatus({ order_internal_no: this.get.order_internal_no });

            if (['1', '2'].includes('' + lockStatus.lockstatusid)) {
                await this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: lockStatus.lockstatusinfo });

                if ('' + lockStatus.lockstatusid !== '2') return;
            }

            this.goTo('orderline', { order_internal_no: this.get.order_internal_no, orderline_keyno: '0' }, event);
        } catch (error) {
            console.error(error);
        }
    }

    public async openNewTextileOrderLine(event?: MouseEvent) {
        if (!this.get) return;

        await this.remember.remember('textileoladd.textilemodel_id_last', '-');
        this.goTo('textileoladd', { order_internal_no: this.get.order_internal_no, orderline_keyno: '0', textilemodel_id: '-' }, event);
    }

    public openEdiOrder(event?: MouseEvent) {
        if (!this.get || +this.get.edi_orderhead_keyno <= 0) return;

        this.goTo('ediorderheads', { argtype: 'edi_orderhead_keyno', argvalue: this.get.edi_orderhead_keyno }, event);
    }

    public async addProductsFromPricelist() {
        await this.runOrderHeadAction('addingProductsFromPricelist', () => this.orderhead.addProductsFromPricelist(this.get!));
    }

    public async removeZeroQuantityLines() {
        await this.runOrderHeadAction('removingZeroQuantityLines', () => this.orderhead.removeZeroQuantityLines(this.get!));
    }

    public async updateOrderPrices() {
        await this.runOrderHeadAction('updatingOrderPrices', () => this.orderhead.updateOrderPrices(this.get!));
    }

    public toggleChosen = async ({ item }: { item?: OrderLineProvideMethodRow | null }) => {
        const orderlineKeyno = item?.item_id ?? item?.orderline_keyno;
        const provideMethodId = item?.provide_method_id;

        if (!this.isFilled(orderlineKeyno) || !this.isFilled(provideMethodId) || this.changingProvideMethod) return;

        try {
            this.changingProvideMethod = true;
            const response = await this.orderhead.changeOrderLineProvideMethod({ orderline_keyno: orderlineKeyno, provide_method_id: provideMethodId });

            if ('' + response.errorcode !== '0') {
                await this.showWarning(response.errormessage);
            } else {
                await this.refreshOrderLines();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.changingProvideMethod = false;
        }
    };

    public async onChangeOrderlineRowSelected(event: RowSelectedEvent<OrderLineProvideMethodRow>) {
        if (event.node.isSelected() !== true) return;

        await this.toggleChosen({ item: event.data });
    }

    public onOrderlineChanged = ({ $value }: { $value: OrderLineLegacyItem[] }) => {
        const sortConfig = this.mconfig.records.find((record) => record.item_name === 'sort');

        if (sortConfig?.isActive !== true || !$value?.length) return;

        for (let i = 1; i < $value.length - 3; i++) {
            const line = $value[i];

            if (line.item_type === 'info') continue;

            const previousName = line.item_name;
            const orderlineNo = String(i).padStart(4, '0');

            line.orderby = i;
            line.item_name = orderlineNo + line.item_name.slice(4);

            if (previousName !== line.item_name) {
                this.orderhead.updateOrderLineNo({ orderline_keyno: line.item_id, orderline_no: i });
            }
        }
    };

    public onDynamicGridReady(grid: GridOptions<OrderLineItem>) {
        this.dynamicGrid = grid;
    }

    public onOrderlineRowSelected(event: any) {
        if (this.get?.setting_show_orderline_calculation !== '1') return;
        if (event.node.isSelected() !== true) return;

        const orderlineKeyno = this.getOrderlineKeyno(event.data);

        if (!orderlineKeyno) return;

        this.selectedOrderlineKeyno = orderlineKeyno;
    }

    public isPrepaymentVisible() {
        if (!this.get) return false;

        return +this.get.amount_payed < +this.get.amount_total_topay && this.get.iscashpayment === '1';
    }

    private async runOrderHeadAction(lock: 'addingProductsFromPricelist' | 'removingZeroQuantityLines' | 'updatingOrderPrices', action: () => Promise<{ errorcode?: string | number; errormessage?: string }>) {
        if (!this.get || this[lock]) return;

        try {
            this[lock] = true;

            const response = await action();

            if ('' + response.errorcode !== '0') {
                await this.showWarning(response.errormessage ?? '');
                return;
            }

            await this.refreshOrderLines();
        } catch (error) {
            console.error(error);
        } finally {
            this[lock] = false;
        }
    }

    private async showWarning(message: string) {
        await this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message });
    }

    private goTo(state: string, params?: Record<string, unknown> | string, event?: MouseEvent) {
        if (!!event && this.utility.isModifierKeyPressed(event)) {
            this.state.newTab(state, params);
        } else {
            this.state.go(state, params);
        }
    }

    private async readLegacyOrderLines() {
        if (!this.params) return;

        this.orderLines = await this.orderhead.getOrderLineList({ order_internal_no: this.params.order_internal_no, view_id: 'w_orderhead.orderline' });
    }

    private readOldGrid() {
        if (!this.params) return;

        if (!this.oldGrid) {
            this.oldGrid = new Grid()
                .setRememberId('w_orderline.grid')
                .setLoadDataFunction({ method: 2645, parameters: () => this.params })
                .setAddRowFunction({ parameters: () => this.params })
                .setSaveDataFunction({ parameters: () => this.params })
                .addRowButton({
                    name: 'customDelete',
                    icon: 'far-trash-alt',
                    btn_type: 'danger',
                    func: async (row) => {
                        await this.oldGrid?.gridfunc?.removeRow(row);
                        this.oldGrid?.gridfunc?.saveChanges(false);
                    },
                });
        } else {
            this.oldGrid.gridfunc?.read();
        }
    }

    private readStandardGrid() {
        if (!this.params) return;

        if (!this.standardGrid) {
            this.standardGrid = this.createOrderLineListGrid();
        } else {
            this.standardGrid.gridfunc?.read();
        }
    }

    private readChangeGrid() {
        if (!this.params) return;

        if (!this.changeGrid) {
            this.changeGrid = this.createOrderLineListGrid();
        } else {
            this.changeGrid.gridfunc?.read();
        }
    }

    private createOrderLineListGrid() {
        return new Grid<OrderLineProvideMethodRow>()
            .setRememberId('orderline.list.ttGrid')
            .setLoadDataFunction({ method: 2215, parameters: () => this.params })
            .setSaveDataFunction({ autoSave: true, parameters: () => ({ datatask_keyno: 2217, order_internal_no: this.params?.order_internal_no }) });
    }

    private async refreshOrderLines() {
        if (!this.get) return;

        if (this.get.setting_orderlines_use_grid === '0') {
            await this.readLegacyOrderLines();
        }

        this.dynamicGrid?.gridfunc?.read();
        this.oldGrid?.gridfunc?.read();
        this.standardGrid?.gridfunc?.read();
        this.changeGrid?.gridfunc?.read();

        if (this.selectedOrderlineKeyno) {
            this.calculationRefreshToken = this.calculationRefreshToken + 1;
        }
    }

    private readCurrentBranch() {
        if (!this.get || !this.params) return;

        if (this.get.setting_orderlines_use_grid === '0') {
            this.readLegacyOrderLines();
            return;
        }

        if (this.displayType === 'change') {
            this.readChangeGrid();
            return;
        }

        if (this.get.setting_orderlines_use_dynamic_grid === '0' && this.get.setting_orderlines_use_new_grid === '1') {
            this.readOldGrid();
        }

        if (this.get.setting_orderlines_use_dynamic_grid === '0' && this.get.setting_orderlines_use_new_grid === '0') {
            this.readStandardGrid();
        }
    }

    private createMconfig() {
        return {
            enabled: true,
            show: true,
            activate: (item: OrderLineMconfigItem | 'all') => {
                if (item === 'all') {
                    this.mconfig.records.filter((record) => record.canActive).forEach((record) => (record.isActive = true));
                } else {
                    item.isActive = true;
                }
            },
            deactivate: (item: OrderLineMconfigItem | 'all') => {
                if (item === 'all') {
                    this.mconfig.records.filter((record) => record.canActive).forEach((record) => (record.isActive = false));
                } else {
                    item.isActive = false;
                }
            },
            function: (item: OrderLineMconfigItem | 'deactivate') => {
                if (item === 'deactivate') {
                    const sort = this.mconfig.records.find((record) => record.item_name === 'sort');
                    if (sort) sort.isActive = false;
                    return;
                }

                if (item.canActive) {
                    item.isActive = !item.isActive;
                }

                if (item.item_id === '0') {
                    const showOptions = item.isActive === true;
                    this.mconfig.records.filter((record) => ['plus', 'minus'].includes(record.item_name)).forEach((record) => (record.show = showOptions));
                }
            },
            records: [
                { item_id: '0', item_name: 'option', glyph: 'glyphicon-option-horizontal', color: 'primary', canActive: true, isActive: false, colorActive: 'warning', enabled: true, show: false },
                { item_id: '1', item_name: 'plus', glyph: 'glyphicon-plus', color: 'primary', show: false },
                { item_id: '2', item_name: 'minus', glyph: 'glyphicon-minus', color: 'primary', show: false },
                { item_id: '3', item_name: 'trash', glyph: 'glyphicon-trash', color: 'primary', show: false },
                { item_id: '4', item_name: 'sort', glyph: 'glyphicon-sort', color: 'primary', canActive: true, isActive: false, colorActive: 'warning', isIcon: false, enabled: false, show: true },
                { item_id: '5', item_name: 'resize', glyph: 'glyphicon-resize-horizontal', color: 'primary', canActive: true, isActive: false, colorActive: 'warning', isIcon: false, enabled: false, show: true },
                { item_id: '6', item_name: 'edit', glyph: 'fa-edit', color: 'primary', canActive: true, isActive: false, colorActive: 'warning', isIcon: false, enabled: false, show: true },
                { item_id: '99', item_name: 'save', glyph: 'glyphicon-save', color: 'primary', animate: null, isIcon: true, enabled: false, show: true },
            ] as OrderLineMconfigItem[],
        };
    }

    private getOrderlineKeyno(row?: OrderLineCellData | null) {
        return row?.orderline_keyno ?? row?.item_id;
    }

    private isFilled(value: unknown): value is string | number {
        return value !== undefined && value !== null && `${value}` !== '';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params'] && changes['params'].previousValue?.order_internal_no !== changes['params'].currentValue?.order_internal_no) {
            this.selectedOrderlineKeyno = undefined;
            this.orderLines = [];
        }

        if (changes['get'] || changes['params']) {
            this.readCurrentBranch();
        }
    }
}

type OrderLineDisplayType = 'std' | 'change';

type OrderLineProvideMethodRow = Partial<OrderLineItem> & Partial<OrderLineLegacyItem> & { provide_method_id?: string | number };

interface OrderLineMconfigItem {
    item_id: string;
    item_name: string;
    glyph: string;
    color: string;
    canActive?: boolean;
    isActive?: boolean;
    colorActive?: string;
    enabled?: boolean;
    show?: boolean;
    isIcon?: boolean;
    animate?: boolean | null;
}

type OrderLineCellData = Partial<OrderLineItem> & Partial<OrderLineLegacyItem> & Record<string, unknown>;
